import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const ROICalculatorSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  
  const [teamSize, setTeamSize] = useState(10);
  const [leadsPerMonth, setLeadsPerMonth] = useState(500);
  const [currentToolsCost, setCurrentToolsCost] = useState(50000);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef?.current) {
      observer?.observe(sectionRef?.current);
    }

    return () => observer?.disconnect();
  }, []);

  const calculateROI = () => {
    const timeSavingsPerPerson = 2;
    const hourlyRate = 500;
    const conversionIncrease = 0.4;
    const averageDealValue = 100000;
    
    const timeSavingsValue = teamSize * timeSavingsPerPerson * hourlyRate * 22;
    const additionalRevenue = leadsPerMonth * conversionIncrease * averageDealValue * 0.1;
    const toolsSavings = currentToolsCost * 0.6;
    
    const totalMonthlySavings = timeSavingsValue + additionalRevenue + toolsSavings;
    const sferaCost = 15000 * teamSize;
    const netBenefit = totalMonthlySavings - sferaCost;
    const roiPercentage = ((netBenefit / sferaCost) * 100)?.toFixed(0);
    const paybackMonths = Math.ceil(sferaCost / netBenefit);

    return {
      timeSavingsValue: Math.round(timeSavingsValue),
      additionalRevenue: Math.round(additionalRevenue),
      toolsSavings: Math.round(toolsSavings),
      totalMonthlySavings: Math.round(totalMonthlySavings),
      sferaCost: Math.round(sferaCost),
      netBenefit: Math.round(netBenefit),
      roiPercentage,
      paybackMonths
    };
  };

  const handleCalculate = () => {
    setShowResults(true);
  };

  const results = calculateROI();

  return (
    <section ref={sectionRef} className="py-24 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-background via-card to-background" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="text-4xl sm:text-5xl font-black text-foreground mb-4">
            Рассчитайте свой ROI
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Узнайте, сколько вы сможете сэкономить и заработать с СФЕРА
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className={`glassmorphism-strong rounded-2xl p-8 transition-all duration-1000 delay-200 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
          }`}>
            <h3 className="text-2xl font-bold text-foreground mb-6">Введите ваши данные</h3>
            
            <div className="space-y-6">
              <div>
                <Input
                  label="Размер команды продаж"
                  type="number"
                  value={teamSize}
                  onChange={(e) => setTeamSize(Number(e?.target?.value))}
                  description={`${teamSize} сотрудников`}
                />
              </div>

              <div>
                <Input
                  label="Лидов в месяц"
                  type="number"
                  value={leadsPerMonth}
                  onChange={(e) => setLeadsPerMonth(Number(e?.target?.value))}
                  description={`${leadsPerMonth} входящих заявок`}
                />
              </div>

              <div>
                <Input
                  label="Текущие расходы на инструменты (₽/мес)"
                  type="number"
                  value={currentToolsCost}
                  onChange={(e) => setCurrentToolsCost(Number(e?.target?.value))}
                  description={`${currentToolsCost?.toLocaleString('ru-RU')} ₽ на CRM, аналитику, автоматизацию`}
                />
              </div>

              <Button
                variant="default"
                size="lg"
                fullWidth
                onClick={handleCalculate}
                iconName="Calculator"
                iconPosition="left"
                className="neon-glow-primary"
              >
                Рассчитать экономию
              </Button>
            </div>
          </div>

          <div className={`glassmorphism-strong rounded-2xl p-8 transition-all duration-1000 delay-400 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
          }`}>
            {showResults ? (
              <div className="space-y-6 animate-fade-in">
                <div className="text-center mb-8">
                  <div className="text-5xl font-black text-primary mb-2">
                    {results?.roiPercentage}%
                  </div>
                  <div className="text-muted-foreground">Возврат инвестиций</div>
                </div>

                <div className="space-y-4">
                  <div className="glassmorphism rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <Icon name="Clock" size={20} color="var(--color-accent)" />
                        <span className="text-sm text-muted-foreground">Экономия времени</span>
                      </div>
                      <span className="text-lg font-bold text-accent">
                        +{results?.timeSavingsValue?.toLocaleString('ru-RU')} ₽
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {teamSize} сотрудников × 2 часа/день × 22 дня
                    </div>
                  </div>

                  <div className="glassmorphism rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <Icon name="TrendingUp" size={20} color="var(--color-success)" />
                        <span className="text-sm text-muted-foreground">Дополнительная выручка</span>
                      </div>
                      <span className="text-lg font-bold text-success">
                        +{results?.additionalRevenue?.toLocaleString('ru-RU')} ₽
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      +40% конверсия × {leadsPerMonth} лидов
                    </div>
                  </div>

                  <div className="glassmorphism rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <Icon name="DollarSign" size={20} color="var(--color-secondary)" />
                        <span className="text-sm text-muted-foreground">Экономия на инструментах</span>
                      </div>
                      <span className="text-lg font-bold text-secondary">
                        +{results?.toolsSavings?.toLocaleString('ru-RU')} ₽
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Замена 5+ систем одной платформой
                    </div>
                  </div>
                </div>

                <div className="glassmorphism rounded-xl p-6 border-2 border-primary/20">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-foreground font-medium">Общая экономия в месяц</span>
                    <span className="text-2xl font-black text-primary">
                      {results?.totalMonthlySavings?.toLocaleString('ru-RU')} ₽
                    </span>
                  </div>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-muted-foreground text-sm">Стоимость СФЕРА</span>
                    <span className="text-foreground font-bold">
                      -{results?.sferaCost?.toLocaleString('ru-RU')} ₽
                    </span>
                  </div>
                  <div className="pt-4 border-t border-border">
                    <div className="flex items-center justify-between">
                      <span className="text-foreground font-bold">Чистая выгода</span>
                      <span className="text-3xl font-black text-success">
                        +{results?.netBenefit?.toLocaleString('ru-RU')} ₽
                      </span>
                    </div>
                  </div>
                </div>

                <div className="text-center pt-4">
                  <div className="inline-flex items-center space-x-2 glassmorphism px-4 py-2 rounded-full">
                    <Icon name="Zap" size={16} color="var(--color-warning)" />
                    <span className="text-sm text-muted-foreground">
                      Окупаемость за <span className="text-warning font-bold">{results?.paybackMonths} месяца</span>
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-full flex items-center justify-center text-center">
                <div>
                  <div className="w-24 h-24 mx-auto mb-6 rounded-2xl bg-primary/10 flex items-center justify-center">
                    <Icon name="Calculator" size={48} color="var(--color-primary)" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-2">
                    Готовы узнать свою выгоду?
                  </h3>
                  <p className="text-muted-foreground">
                    Заполните форму слева и нажмите "Рассчитать экономию"
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ROICalculatorSection;